import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import ElementComponent from '../../../Component/UI/Element';
import { Easing } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';
import Sprite from '../../../Prototype/Sprite';
import Point from '../../../Utility/Point';
import { Random } from '../../../Utility/Random';
import TransformComponent from '../../../Component/Transform';
import {
    AwaitIterators,
    ConcatIterators,
    PlayAnimation,
} from '../AnimationHelper';
import { MathHelper } from '../../../Utility/MathHelper';
import UITextComponent from '../../../Component/UI/Text';
import UIImageComponent from '../../../Component/UI/Image';
import {
    StarChestEvent, StarChestHelper
} from '../../../Utility/StarChestHelper';
import { FillBar } from './FillBar';
import WorldSystem from '../../World/WorldSystem';
import { ProgressBarChestUpgrade } from './ProgressBarChestUpgrade';
import StarChest from '../../../Prefabs/StarChest/StarChest';
import { PlaySound } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import Game from '../../../Game';
import LevelNode from '../../../Prototype/World/LevelNode/LevelNode';
import LevelNodePreventCacheReasons from '../../../Prototype/World/LevelNode/LevelNodePreventCacheReasons';

interface StarChestRewardParams {
    sourceObject: GameObject;
    amount: number;
    previousStarCount: number;
    currentStarChestTier: number;
    system: WorldSystem;
}
export class RewardStarChestStars extends AnimationClip<StarChestRewardParams> {
    public *execute(
        gameobject: GameObject,
        meta: StarChestRewardParams
    ): IterableIterator<number> {
        if (gameobject instanceof LevelNode) {
            gameobject.reasonsToNotCacheAsBitmap.add(
                LevelNodePreventCacheReasons.RewardStars
            );
        }
        const newStarChestStarCount = meta.previousStarCount + meta.amount;

        // Cache elements and components
        const progressBar = gameobject.find(
            'star_chest_progress_bar_container'
        );
        const progressBarElement = progressBar.getComponent(ElementComponent);
        const starText = progressBar.find(
            'progress_bar/progress_bar_text_group/progress_bar_text'
        );
        const starTextComponent = starText.getComponent(UITextComponent);
        const fill = progressBar.findChildByName('progress_bar_fill');
        const currentChest = progressBar.find('current_icon');
        const currentChestComponent = currentChest.getComponent(
            UIImageComponent
        );
        const nextChest = progressBar.find('next_icon');
        const nextChestComponent = nextChest.getComponent(UIImageComponent);

        const currentOrPreviousTier = StarChestHelper.getCurrentOrPreviousTier(
            meta.currentStarChestTier
        );
        const nextOrLastTier = StarChestHelper.getNextOrLastTier(
            meta.currentStarChestTier
        );

        // Update chests on progress bar
        currentChestComponent.texture = StarChestHelper.getStarChestProgressBarTexture(
            currentOrPreviousTier
        );
        nextChestComponent.texture = StarChestHelper.getStarChestProgressBarTexture(
            nextOrLastTier
        );
        const nextOrLastTierData = StarChestHelper.getChestTierData(
            nextOrLastTier
        );

        // Update the text and fill percentage before showing
        starTextComponent.text = `${meta.previousStarCount} / ${nextOrLastTierData.requiredStars}`;
        FillBar.setProgress(
            fill,
            getProgressToNextStarChest(
                meta.currentStarChestTier,
                meta.previousStarCount
            )
        );

        // Show bar
        yield* this.animateProgressBarShowTo(progressBarElement, true);

        yield* delay(200);

        // Create stars and scale them in
        const stars = this.spawnStars(meta);
        yield* AwaitIterators(
            stars.map(star =>
                this.startTween(
                    this.getScaleTween(star, 2, Random.int(-50, 50) + 150)
                )
            )
        );

        // Get destination
        const starDestination = progressBar.findChildByName(
            'progress_bar_star_destination'
        );

        // Add to destination object so we can animate towards position (0,0)
        stars.forEach(star => starDestination.addChild(star));

        // Wait while stars travel
        yield* AwaitIterators(
            stars.map(
                function*(star: Sprite, index: number) {
                    const starFlyDuration = 750 + 300 * index;
                    yield* AwaitIterators([
                        this.startTween(
                            this.getBezierTween(
                                star,
                                starFlyDuration,
                                Point.Zero
                            )
                        ),
                        ConcatIterators([
                            delay(starFlyDuration - 150),
                            (function*() {
                                PlaySound([
                                    'add-star-01',
                                    'add-star-02',
                                    'add-star-03',
                                ][index]);
                            })(),
                        ]),
                    ]);
                    star.destroy();

                    yield* this.startTween(
                        this.getScaleTween(starText, 1.5, 150)
                    );

                    const clampedStarCount = MathHelper.clamp(
                        meta.previousStarCount + 1 + index,
                        0,
                        nextOrLastTierData.requiredStars
                    );

                    yield* AwaitIterators([
                        this.incrementProgress(
                            starTextComponent,
                            fill,
                            clampedStarCount,
                            nextOrLastTierData.requiredStars,
                            meta.currentStarChestTier
                        ),
                        this.startTween(this.getScaleTween(starText, 1, 100)),
                    ]);
                }.bind(this)
            )
        );

        const progress = getProgressToNextStarChest(
            meta.currentStarChestTier,
            newStarChestStarCount
        );

        yield* delay(150);
        // If tier is completed and there is another tier after
        if (
            progress >= 1 &&
            StarChestHelper.tierExists(meta.currentStarChestTier + 1)
        ) {
            const upgradedNextOrLastTier = StarChestHelper.getNextOrLastTier(
                nextOrLastTier
            );
            const upgradedNextOrLastTierData = StarChestHelper.getChestTierData(
                upgradedNextOrLastTier
            );

            const clampedStarCount = MathHelper.clamp(
                newStarChestStarCount,
                0,
                upgradedNextOrLastTierData.requiredStars
            );
            // Reset progressbar to 0%
            yield* this.incrementProgress(
                starTextComponent,
                fill,
                nextOrLastTierData.requiredStars,
                upgradedNextOrLastTierData.requiredStars,
                nextOrLastTier
            );
            yield* delay(50);
            // Fill with remaining stars
            yield* this.incrementProgress(
                starTextComponent,
                fill,
                clampedStarCount,
                upgradedNextOrLastTierData.requiredStars,
                nextOrLastTier
            );
            yield* delay(300);

            const animatedChestContainer = gameobject.find('animated_chest');
            yield* this.createAndAnimateNewStarChest(
                animatedChestContainer,
                progressBar,
                currentChest,
                nextChest,
                meta.currentStarChestTier
            );
        }

        yield* delay(750);

        // Hide bar
        yield* this.animateProgressBarShowTo(progressBarElement, false);

        if (gameobject instanceof LevelNode) {
            gameobject.reasonsToNotCacheAsBitmap.remove(
                LevelNodePreventCacheReasons.RewardStars
            );
        }
    }

    private *createAndAnimateNewStarChest(
        animatedChestContainer: GameObject,
        progressBar: GameObject,
        currentChest: GameObject,
        nextChest: GameObject,
        tier: number
    ) {
        const starChestAnimated = GameObject.Instantiate(StarChest, {
            name: 'star_chest',
            layout: {
                anchors: [0.05, 0.5, 0.05, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [0.5, 0.5],
            },
            tier,
        });
        animatedChestContainer.addChild(starChestAnimated);
        yield* PlayAnimation(
            new ProgressBarChestUpgrade(animatedChestContainer, {
                tier: tier + 1,
                progressBar,
                starChest: starChestAnimated,
                progressBarLeftIcon: currentChest,
                progressBarRightIcon: nextChest,
            })
        );
        starChestAnimated.destroy();
    }

    private *animateProgressBarShowTo(
        element: ElementComponent,
        show: boolean
    ): IterableIterator<number> {
        const newOffset: number = 60 * (show ? -1 : 1);
        const tween = this.getTween(element)
            .to({ offsetY1: newOffset, offsetY2: newOffset }, 300)
            .easing(Easing.Quadratic.Out);
        yield* this.startTween(tween);
    }
    private spawnStars(params: StarChestRewardParams): Sprite[] {
        const stars: Sprite[] = [];
        for (let i = 0; i < params.amount; i++) {
            const star = GameObject.Instantiate(Sprite, {
                texture: 'level_node_star_2',
                x: 0,
                y: 0,
                anchor: new Point(0, 0),
                scale: new Point(0, 0),
            });
            params.sourceObject.addChild(star);
            stars.push(star);
        }
        return stars;
    }
    private getScaleTween(
        gameObject: GameObject,
        scale: number,
        duration: number
    ): TWEEN.Tween {
        const scaleComponenet = gameObject.hasComponent(ElementComponent)
            ? gameObject.getComponent(ElementComponent)
            : gameObject.getComponent(TransformComponent);
        const scaleTween = this.getTween(scaleComponenet.scale).to(
            {
                x: scale,
                y: scale,
            },
            duration
        );
        return scaleTween;
    }
    private getBezierTween(
        gameObject: GameObject,
        duration: number,
        p4: Point
    ) {
        const transform = gameObject.getComponent(TransformComponent);
        const p1 = transform.position.clone();
        const p2 = new Point(-100, p1.y + 100);
        const p3 = new Point(100, 100);

        const bezierTween = this.getTween({ progress: 0 });
        bezierTween.to({ progress: 1 }, duration).onUpdate(t => {
            const xy = MathHelper.cubicBezier(t.progress, p1, p2, p3, p4);
            transform.position.set(xy.x, xy.y);
        });
        return bezierTween;
    }

    private incrementProgress(
        textComponent: UITextComponent,
        fill: GameObject,
        starCount: number,
        requiredStars: number,
        currentTier: number
    ) {
        const progress = getProgressToNextStarChest(currentTier, starCount);

        textComponent.text = `${starCount} / ${requiredStars}`;
        Game.scene
            .getActiveScene()
            .emit(StarChestEvent.REWARD_STAR, { starCount });

        return PlayAnimation(
            new FillBar(fill, {
                progress,
                duration: 500,
            })
        );
    }
}

function getProgressToNextStarChest(currentTier: number, starCount: number) {
    return StarChestHelper.getProgressToNextStarChest(
        currentTier,
        starCount,
        0.15,
        0.0125
    );
}
